import { InMemoryCache } from '@apollo/client';
import { getDataFetchingClientContext } from 'data-fetching-client';
import { useContext, useMemo } from 'react';
export function addTypePoliciesToClient({
  client,
  queryFieldPolicies,
  mutationFieldPolicies
}) {
  if (!queryFieldPolicies && !mutationFieldPolicies) {
    return;
  }
  if ((client === null || client === void 0 ? void 0 : client.cache) instanceof InMemoryCache) {
    client.cache.policies.addTypePolicies({
      Query: {
        fields: queryFieldPolicies
      },
      Mutation: {
        fields: mutationFieldPolicies
      }
    });
  } else {
    throw new Error('DataFetchingClient cache is not an InMemoryCache, addTypePolicies may not succeed');
  }
}

/**
 * Docs: https://www.apollographql.com/docs/react/caching/cache-configuration#customizing-type-policies
 *
 * Some libraries use DFC without instantiating the client themselves, instead relying on consumers to
 * instantiate the client and render the library components in its context.
 * If a library were to render its own DFC provider/instance, then any components passed into the library's
 * react tree (via children, component props, or render props) would not be rendered within the context of the
 * consumer's DFC instance.
 *
 * <ConsumerApp>
 *   <ConsumerDFCProvider>
 *     <Lib>
 *       <LibDFCProvider>
 *         <ConsumerComponent /> <-- any useQuery() calls here will hit the lib's DFC instance, missing any of the consumer's type policies
 *
 * So libs often defer to the consumer's DFC instance.
 *
 *  Aside:
 *    Another option might be to wrap each <ConsumerComponent /> in a DFCProvider which passes through the outer DFC instance,
 *    shielding the consumer's components from the lib's DFC instance. This can be onerous though, particularly with multiple
 *    levels of nesting.
 *
 * What happens when the lib needs to configure type policies?
 * Currently the lib needs to require consumers to import a type policy and pass it to the DFC constructor's configuration.
 * There's no real way to type check this; it pretty much fully relies on consumers reading docs/READMEs
 *
 * Apollo, however, provides a method for adding type policies to a cache that has already been constructed.
 * Unfortunately, DFC's types don't expose this api; the fact that it uses an InMemoryCache is an implementation detail,
 * which we must rely on for this approach to work.
 * We can at least have a runtime check + tests to warn us if this implementation detail ever changes.
 *
 * See also discussion here for handling this officially in data-fetching-client:
 * https://git.hubteam.com/HubSpot/data-fetching-client/pull/270
 *
 * @param client - The DFC instance to add type policies to. If not provided, the hook will use the DFC instance in context
 * @param queryFieldPolicies - The type policies to add to the query field.
 * @param mutationFieldPolicies - The type policies to add to the mutation field.
 *
 */
export function useAddTypePolicies({
  client,
  queryFieldPolicies,
  mutationFieldPolicies
}) {
  var _useContext;
  const dfcClient = (_useContext = useContext(getDataFetchingClientContext())) === null || _useContext === void 0 ? void 0 : _useContext.client;
  const resolvedClient = client !== null && client !== void 0 ? client : dfcClient;
  useMemo(() => {
    addTypePoliciesToClient({
      client: resolvedClient,
      queryFieldPolicies,
      mutationFieldPolicies
    });
  }, [resolvedClient, queryFieldPolicies, mutationFieldPolicies]);
}